/**
 * Token accounting and what a generation is worth in credits.
 *
 * The technical task is blunt about this: "you cannot price this without real
 * numbers, and your first estimate will be wrong." So this module is built to be
 * *corrected* rather than to be right — every model call records its usage,
 * `estimateCostMicroUsd` turns usage into money with no guesswork, and the price
 * in credits is a pure function of measured cost and a margin multiplier that
 * lives in one place.
 *
 * All money is in **micro-dollars** (1 USD = 1_000_000). Integers throughout:
 * per-token prices in cents would round to zero, and floating-point money is how
 * ledgers end up off by a fraction of a cent per row.
 */
import { z } from 'zod';
export const MICRO_USD_PER_USD = 1_000_000;
export const MODEL_PRICING = {
    'claude-opus-5': {
        inputPerMTok: 5 * MICRO_USD_PER_USD,
        outputPerMTok: 25 * MICRO_USD_PER_USD,
        cacheReadPerMTok: 0.5 * MICRO_USD_PER_USD,
        cacheWritePerMTok: 6.25 * MICRO_USD_PER_USD,
    },
    'claude-sonnet-5': {
        inputPerMTok: 3 * MICRO_USD_PER_USD,
        outputPerMTok: 15 * MICRO_USD_PER_USD,
        cacheReadPerMTok: 0.3 * MICRO_USD_PER_USD,
        cacheWritePerMTok: 3.75 * MICRO_USD_PER_USD,
    },
    'claude-haiku-4-5': {
        inputPerMTok: 1 * MICRO_USD_PER_USD,
        outputPerMTok: 5 * MICRO_USD_PER_USD,
        cacheReadPerMTok: 0.1 * MICRO_USD_PER_USD,
        cacheWritePerMTok: 1.25 * MICRO_USD_PER_USD,
    },
};
export const DEFAULT_MODEL = 'claude-opus-5';
export const tokenUsageSchema = z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    cacheReadTokens: z.number().int().nonnegative().default(0),
    cacheWriteTokens: z.number().int().nonnegative().default(0),
});
export const EMPTY_USAGE = {
    inputTokens: 0,
    outputTokens: 0,
    cacheReadTokens: 0,
    cacheWriteTokens: 0,
};
export const addUsage = (a, b) => ({
    inputTokens: a.inputTokens + b.inputTokens,
    outputTokens: a.outputTokens + b.outputTokens,
    cacheReadTokens: a.cacheReadTokens + b.cacheReadTokens,
    cacheWriteTokens: a.cacheWriteTokens + b.cacheWriteTokens,
});
/**
 * Exact cost of one call, rounded up.
 *
 * Rounding up rather than to nearest: under-reporting our own cost is the error
 * that quietly loses money, and the bias is a fraction of a cent per call.
 */
export function estimateCostMicroUsd(usage, model = DEFAULT_MODEL) {
    const pricing = MODEL_PRICING[model] ?? MODEL_PRICING[DEFAULT_MODEL];
    const total = usage.inputTokens * pricing.inputPerMTok +
        usage.outputTokens * pricing.outputPerMTok +
        usage.cacheReadTokens * pricing.cacheReadPerMTok +
        usage.cacheWriteTokens * pricing.cacheWritePerMTok;
    return Math.ceil(total / 1_000_000);
}
// ── Credits ──────────────────────────────────────────────────────────────────
/**
 * What one credit is worth in revenue, taken from the middle pack
 * (500 credits for $50.00). Derived rather than declared so the two cannot drift.
 */
export const CREDIT_RETAIL_MICRO_USD = (50_00 * (MICRO_USD_PER_USD / 100)) / 500;
/**
 * Gross margin multiple on measured model cost.
 *
 * 3x covers the model call plus the build, storage, bandwidth and support the
 * credit also has to pay for. It is a starting point, not a finding.
 */
export const GENERATION_MARGIN = 3;
/**
 * What a generation should cost, given what it actually cost us.
 *
 * Rounds up to a whole credit and never returns zero for non-free work, because
 * a generation that costs nothing is a generation someone will run in a loop.
 */
export function priceInCredits(costMicroUsd, margin = GENERATION_MARGIN) {
    if (costMicroUsd <= 0)
        return 0;
    return Math.max(1, Math.ceil((costMicroUsd * margin) / CREDIT_RETAIL_MICRO_USD));
}
/**
 * The per-generation-type estimates the product currently charges.
 *
 * ⚠️ These are **projections, not measurements**. They assume roughly 20k input
 * and 28k output tokens for a create, with the system prompt and reference
 * corpus served from cache. At Opus 5 rates that is ~$0.80 of model cost, which
 * at a 3x margin is ~24 credits — far above the 5 credits Phase 1 shipped.
 *
 * `generations.tokens_in` / `tokens_out` are recorded on every call precisely so
 * this table can be replaced with measured percentiles. Until it is, treat the
 * Phase 1 prices as a known-unprofitable placeholder rather than a decision.
 */
export const ESTIMATED_GENERATION_COST_MICRO_USD = {
    create: 800_000,
    edit: 220_000,
    repair: 90_000,
};
/** Convenience for logs and the dashboard. */
export const formatMicroUsd = (microUsd) => `$${(microUsd / MICRO_USD_PER_USD).toFixed(4)}`;
// ── Spend breaker ────────────────────────────────────────────────────────────
/**
 * Rolling spend meters, in Redis.
 *
 * Incremented by the worker on **every model call**, not by the API on every
 * settled generation. The difference matters: the failure this exists to stop is
 * a retry loop or a prompt-injection loop that never produces a finished
 * generation at all, so a meter that only counts completed work would read zero
 * while the bill climbed all night.
 *
 * Keys carry their window so they expire on their own and no sweeper is needed.
 */
export const spendKeyHour = (isoHour) => `spend:hour:${isoHour}`;
export const spendKeyDay = (isoDay) => `spend:day:${isoDay}`;
export const userSpendKeyDay = (userId, isoDay) => `spend:user:${userId}:${isoDay}`;
/** `2026-08-12T09` and `2026-08-12`, both UTC. */
export const spendWindows = (at) => {
    const iso = at.toISOString();
    return { hour: iso.slice(0, 13), day: iso.slice(0, 10) };
};
export const SPEND_KEY_TTL_SECONDS = { hour: 3 * 3600, day: 3 * 86_400 };
/**
 * Defaults chosen to be survivable, not generous.
 *
 * At the measured ~$0.11 per fixture generation and a projected ~$0.80 per real
 * one, a $200 day is roughly 250 real generations — comfortably above any honest
 * early usage and far below what an unattended loop can spend overnight. Both are
 * overridable per environment; the point of a breaker is that somebody chose the
 * number deliberately.
 */
export const DEFAULT_SPEND_CEILING_MICRO_USD = {
    hour: 50_000_000,
    day: 200_000_000,
    userDay: 20_000_000,
};
/** Warn well before refusing, so a human can look before anything is blocked. */
export const SPEND_WARN_FRACTION = 0.8;
export function checkSpend(state) {
    if (state.day >= state.ceilingDay) {
        return { ok: false, window: 'day', spent: state.day, ceiling: state.ceilingDay };
    }
    if (state.hour >= state.ceilingHour) {
        return { ok: false, window: 'hour', spent: state.hour, ceiling: state.ceilingHour };
    }
    return {
        ok: true,
        warn: state.day >= state.ceilingDay * SPEND_WARN_FRACTION ||
            state.hour >= state.ceilingHour * SPEND_WARN_FRACTION,
    };
}
//# sourceMappingURL=cost.js.map